import Fuse from "fuse.js";

type FuseKey = string | { name: string; weight?: number };

type FuzzySearchOptions<T> = {
  keys: FuseKey[];
  threshold?: number;
  limit?: number;
  minMatchCharLength?: number;
  shouldSort?: boolean;
  getFn?: (obj: T, path: string | string[]) => readonly string[] | string;
};

type FuzzyMatcher<T> = {
  search(query: string): T[];
  setItems(items: T[]): void;
  raw(): Fuse<T>;
};

declare global {
  interface Window {
    WMFuzzy?: {
      createMatcher<T>(items: T[], options: FuzzySearchOptions<T>): FuzzyMatcher<T>;
      search<T>(items: T[], query: string, options: FuzzySearchOptions<T>): T[];
    };
  }
}

function normalizeQuery(rawQuery: string): string {
  return String(rawQuery || "").trim();
}

function buildFuse<T>(items: T[], options: FuzzySearchOptions<T>): Fuse<T> {
  return new Fuse(items, {
    keys: options.keys as string[],
    threshold: options.threshold ?? 0.34,
    ignoreLocation: true,
    includeScore: true,
    minMatchCharLength: options.minMatchCharLength ?? 2,
    shouldSort: options.shouldSort ?? true,
    getFn: options.getFn,
  });
}

export function createFuzzyMatcher<T>(items: T[], options: FuzzySearchOptions<T>): FuzzyMatcher<T> {
  let source = Array.isArray(items) ? [...items] : [];
  let fuse = buildFuse(source, options);

  return {
    search(query: string): T[] {
      const normalized = normalizeQuery(query);
      if (!normalized) return [...source];
      const limit = options.limit ?? source.length;
      return fuse.search(normalized, { limit }).map((result) => result.item);
    },
    setItems(itemsList: T[]): void {
      source = Array.isArray(itemsList) ? [...itemsList] : [];
      fuse = buildFuse(source, options);
    },
    raw(): Fuse<T> {
      return fuse;
    },
  };
}

export function fuzzySearch<T>(items: T[], query: string, options: FuzzySearchOptions<T>): T[] {
  return createFuzzyMatcher(items, options).search(query);
}

window.WMFuzzy = {
  createMatcher: createFuzzyMatcher,
  search: fuzzySearch,
};
